import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, DiscordLogo, EnvelopeSimple, GameController, CurrencyDollar, Copy } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import api from "@/lib/api";

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="kbd-label mb-1">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-white/40 mt-1 font-mono">{hint}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, iconColor = "#00F0FF", children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 border-b border-white/10 pb-2">
        {Icon && <Icon size={16} color={iconColor} weight="fill" />}
        <h3 className="display-heading font-bold text-sm">{title}</h3>
      </div>
      {children}
    </section>
  );
}

export default function MemberDrawer({ discordId, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [creators, setCreators] = useState([]);
  const [attrCreatorId, setAttrCreatorId] = useState("");
  const [savingAttr, setSavingAttr] = useState(false);

  useEffect(() => {
    if (!discordId) { setData(null); return; }
    (async () => {
      try {
        const [r, cr] = await Promise.all([
          api.get(`/members/${discordId}`),
          api.get(`/creators`),
        ]);
        setData(r.data);
        setCreators(cr.data || []);
        setAttrCreatorId(r.data.creator?.id || "");
        // Prefer manually-set values; auto-fill from suggested (Apollo/Tebex) when empty.
        const s = r.data.suggested || {};
        setDraft({
          email: r.data.email || s.email || "",
          fivem_license: r.data.fivem_license || s.fivem_license || "",
          fivem_name: r.data.fivem_name || s.fivem_name || "",
          notes: r.data.notes || "",
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load member");
        onClose();
      }
    })();
  }, [discordId, onClose]);

  async function applyAttribution() {
    setSavingAttr(true);
    try {
      const r = await api.post(`/members/${discordId}/attribution`, { creator_id: attrCreatorId || null });
      setData(r.data);
      setAttrCreatorId(r.data.creator?.id || "");
      toast.success(attrCreatorId ? "Attribution re-assigned" : "Attribution cleared");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update attribution");
    } finally { setSavingAttr(false); }
  }

  async function save() {
    setSaving(true);
    try {
      const body = {};
      Object.keys(draft).forEach((k) => {
        if (draft[k] !== (data[k] ?? "")) body[k] = draft[k];
      });
      if (Object.keys(body).length === 0) { toast.info("No changes"); setSaving(false); return; }
      const r = await api.patch(`/members/${discordId}`, body);
      setData(r.data);
      toast.success("Saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }

  function upd(k, v) { setDraft((d) => ({ ...d, [k]: v })); }
  function copy(t) { navigator.clipboard.writeText(t); toast.success("Copied"); }

  return (
    <Sheet open={!!discordId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="!w-full sm:!w-[540px] !max-w-none bg-obsidian-surface border-l border-white/10 overflow-y-auto text-white">
        <SheetHeader>
          <SheetTitle className="display-heading text-2xl">
            {data ? `@${data.username || "unknown"}` : "Loading…"}
          </SheetTitle>
          <SheetDescription className="kbd-label text-white/40">
            Cross-account member profile — Discord, FiveM, purchases & enrichment
          </SheetDescription>
        </SheetHeader>

        {data && (
          <div className="mt-6 space-y-8">
            <Section title="IDENTITY" icon={DiscordLogo} iconColor="#5865F2">
              <div className="grid grid-cols-2 gap-3 text-sm font-mono">
                <div><div className="kbd-label">DISCORD ID</div><button onClick={() => copy(data.discord_id)} className="text-white/80 hover:text-neon-cyan inline-flex items-center gap-1">{data.discord_id} <Copy size={11} /></button></div>
                <div><div className="kbd-label">USERNAME</div>@{data.username || "—"}</div>
                <div className="col-span-2"><div className="kbd-label">ATTRIBUTED TO</div>
                  {data.creator ? (
                    <div><span className="font-sans text-white">{data.creator.display_name}</span> <span className="text-white/40">@{data.creator.slug}</span></div>
                  ) : <span className="text-white/40">Unattributed</span>}
                </div>
                <div><div className="kbd-label">FIRST VERIFIED</div>{data.attribution?.first_verified_at?.slice(0, 19).replace("T", " ") || "—"}</div>
                <div><div className="kbd-label">SOURCE</div>{data.attribution?.source || data.attribution?.code || "—"}</div>
              </div>
            </Section>

            <Section title="ATTRIBUTION (OWNER OVERRIDE)" icon={DiscordLogo} iconColor="#00F0FF">
              <Field label="ATTRIBUTED CREATOR" hint="Overrides first-touch. Re-assign to a creator, or set Unattributed to clear the lock (member becomes re-attributable on their next join).">
                <div className="flex gap-2">
                  <select value={attrCreatorId} onChange={(e) => setAttrCreatorId(e.target.value)} data-testid="member-attribution-select"
                    className="flex-1 bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none">
                    <option value="">— Unattributed —</option>
                    {creators.map((c) => <option key={c.id} value={c.id}>{c.display_name} (@{c.slug})</option>)}
                  </select>
                  <button onClick={applyAttribution} disabled={savingAttr || attrCreatorId === (data.creator?.id || "")} data-testid="member-attribution-apply"
                    className="neon-btn px-4 py-2 kbd-label disabled:opacity-50 whitespace-nowrap">{savingAttr ? "…" : (attrCreatorId ? "RE-ASSIGN" : "CLEAR")}</button>
                </div>
              </Field>
            </Section>

            <Section title="GUILD" icon={DiscordLogo} iconColor="#5865F2">
              <div className="grid grid-cols-2 gap-3 text-sm font-mono">
                <div><div className="kbd-label">STATUS</div>
                  {data.guild?.is_member ? <span className="text-emerald-400">MEMBER</span> :
                   data.guild?.left_at ? <span className="text-red-400">LEFT</span> :
                   data.guild?.first_joined_at ? <span className="text-white/60">FORMERLY MEMBER</span> :
                   <span className="text-white/40">NEVER JOINED</span>}
                </div>
                <div><div className="kbd-label">FIRST JOINED</div>{data.guild?.first_joined_at?.slice(0, 10) || "—"}</div>
                <div><div className="kbd-label">LAST JOINED</div>{data.guild?.last_joined_at?.slice(0, 10) || "—"}</div>
                <div><div className="kbd-label">LEFT AT</div>{data.guild?.left_at?.slice(0, 10) || "—"}</div>
              </div>
            </Section>

            <Section title="CONTACT & ACCOUNTS" icon={EnvelopeSimple}>
              {data.suggested && (data.suggested.email || data.suggested.fivem_license || data.suggested.fivem_name || data.suggested.steam_id || data.suggested.customer_name) && (
                <div className="border border-neon-cyan/30 bg-neon-cyan/5 px-3 py-2 mb-3 text-[11px] font-mono text-white/70" data-testid="member-autofill-hint">
                  <div className="kbd-label text-neon-cyan mb-1">AUTO-FILLED FROM APOLLO / TEBEX</div>
                  {data.suggested.customer_name && <div>Name: <span className="text-white">{data.suggested.customer_name}</span></div>}
                  {data.suggested.email && <div>Email: <span className="text-white">{data.suggested.email}</span> <span className="text-white/40">← from Tebex purchase</span></div>}
                  {data.suggested.fivem_name && <div>FiveM name: <span className="text-white">{data.suggested.fivem_name}</span> <span className="text-white/40">← from Apollo</span></div>}
                  {data.suggested.fivem_license && <div>License: <span className="text-white">{data.suggested.fivem_license.slice(0, 12)}…</span> <span className="text-white/40">← from Apollo</span></div>}
                  {data.suggested.steam_id && <div>Steam: <span className="text-white">{data.suggested.steam_id}</span></div>}
                  {data.suggested.fivem_id && <div>Cfx ID: <span className="text-white">{data.suggested.fivem_id}</span></div>}
                  {data.suggested.ip && <div>Last IP: <span className="text-white">{data.suggested.ip}</span></div>}
                  <div className="text-white/40 mt-1">Fields below are pre-filled — click SAVE to lock them in, or edit first.</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="EMAIL" hint={data.suggested?.email && !data.email ? "Suggested from Tebex — save to keep" : "Populated from CSV uploads or entered manually"}>
                  <input value={draft.email} onChange={(e) => upd("email", e.target.value)} placeholder="user@example.com" className="w-full bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none" />
                </Field>
                <Field label="FIVEM LICENSE" hint={data.suggested?.fivem_license && !data.fivem_license ? "Suggested from Apollo — save to keep" : "license:xxxxxxx from the FiveM identifier"}>
                  <input value={draft.fivem_license} onChange={(e) => upd("fivem_license", e.target.value)} placeholder="license:abc123…" className="w-full bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none" />
                </Field>
                <Field label="FIVEM DISPLAY NAME" hint={data.suggested?.fivem_name && !data.fivem_name ? "Suggested from Apollo — save to keep" : undefined}>
                  <input value={draft.fivem_name} onChange={(e) => upd("fivem_name", e.target.value)} className="w-full bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none" />
                </Field>
                <Field label="FIVEM PLAYTIME (aggregated)">
                  <div className="font-mono text-sm bg-black/40 px-3 py-2 border border-white/5">{Math.round(data.fivem?.total_minutes || 0)}m across {data.fivem?.sessions?.length || 0} sessions</div>
                </Field>
              </div>
            </Section>

            <Section title="PURCHASES" icon={CurrencyDollar} iconColor="#E4FF00">
              <div className="grid grid-cols-3 gap-3 mb-3 text-sm font-mono">
                <div className="card-tactical p-3"><div className="kbd-label">GROSS</div><div className="stat-value text-xl mt-1 text-emerald-400">${data.purchase_totals.gross}</div></div>
                <div className="card-tactical p-3"><div className="kbd-label">REFUNDS</div><div className="stat-value text-xl mt-1 text-red-400">${data.purchase_totals.refunds}</div></div>
                <div className="card-tactical p-3"><div className="kbd-label">NET</div><div className="stat-value text-xl mt-1 text-neon-yellow">${data.purchase_totals.net}</div></div>
              </div>
              {data.purchases.length > 0 ? (
                <table className="w-full text-xs font-mono">
                  <thead><tr className="kbd-label text-left text-white/50"><th className="py-1">TX</th><th>KIND</th><th>AMT</th><th>BATCH</th><th>WHEN</th></tr></thead>
                  <tbody>
                    {data.purchases.slice(0, 12).map(p => (
                      <tr key={p.transaction_id} className="border-t border-white/5">
                        <td className="py-1 truncate max-w-[120px]">{p.transaction_id}</td>
                        <td className={p.kind === "payment" ? "text-emerald-400" : "text-red-400"}>{p.kind}</td>
                        <td>${p.amount}</td>
                        <td className="text-neon-cyan">{p.batch_label || p.source}</td>
                        <td className="text-white/50">{(p.raw_date || p.created_at)?.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div className="text-white/40 text-sm font-mono">No purchases on file.</div>}
            </Section>

            <Section title="NOTES">
              <textarea rows={4} value={draft.notes} onChange={(e) => upd("notes", e.target.value)} placeholder="Anything to remember about this user — support tickets, chargebacks, VIP status…" className="w-full bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none resize-none" />
            </Section>

            <div className="flex gap-3 pt-4 border-t border-white/10 sticky bottom-0 bg-obsidian-surface pb-2">
              <button onClick={save} disabled={saving} data-testid="member-save" className="neon-btn flex-1 py-3 disabled:opacity-50">{saving ? "SAVING…" : "SAVE"}</button>
              <button onClick={onClose} className="ghost-btn px-4 py-3 text-white kbd-label"><X size={14} /></button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
